import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getPartnere } from '../lib/cms';
import WhiteLogo from '../components/WhiteLogo';

export default function Partnere() {
  const [partnere, setPartnere] = useState([]);

  useEffect(() => {
    getPartnere().then(setPartnere);
  }, []);

  const premier = partnere.filter(p => p.kategori === 'Premier Sponsors');
  const elite = partnere.filter(p => p.kategori === 'Elite Sponsors');
  const core = partnere.filter(p => p.kategori === 'Core Sponsors');
  const supporter = partnere.filter(p => p.kategori === 'Supporter Sponsors');

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Partnere - Foreningen ØrBike</title>
        <meta
          name="description"
          content="Støt dem, der får ØrBike til at køre! Se vores fantastiske partnere, sponsorer og samarbejdspartnere i Ørbæk Midtpunkt."
        />
        <meta property="og:title" content="Partnere - Foreningen ØrBike" />
        <meta property="og:description" content="Se sponsorer og partnere der støtter Danmarks vildeste indoor bike fest." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#181024] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4">
            Tak for støtten
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            Vores fantastiske partnere
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            Støt dem, der får ØrBike til at køre!
          </p>
        </div>
      </section>

      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Intro */}
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <p className="text-slate-300 text-base leading-relaxed">
            ØrBike ville ikke være det samme uden vores <strong>fantastiske partnere, sponsorer og samarbejdspartnere</strong>, som gør eventet muligt år efter år.
          </p>
          <p className="text-slate-400 text-sm leading-relaxed">
            Med deres støtte – økonomisk, praktisk og med udstyr – hjælper de os med at skabe rammerne for en dag fyldt med <strong>energi, fællesskab og bevægelse for en god sag</strong>.
          </p>
          <p className="text-slate-400 text-sm leading-relaxed">
            Et særligt tak til de samarbejdspartnere, der <strong>låner cykler og udstyr</strong>, så vi kan fylde salen og give over <strong>1.000 deltagere</strong> en uforglemmelig oplevelse i Ørbæk Midtpunkt.
          </p>
        </div>

        {/* Premier Sponsors (Fremhævet større) */}
        {premier.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 border-b border-pink-500/30 pb-3">
              <span className="w-3 h-3 rounded-full bg-pink-500 animate-pulse"></span>
              <h3 className="text-2xl sm:text-3xl font-black font-display text-white uppercase">
                Premier Sponsors
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {premier.map((p, i) => (
                <div
                  key={p.id || i}
                  className="bg-gradient-to-b from-[#181a29] to-[#10121d] border-2 border-pink-500/40 rounded-3xl p-8 flex flex-col items-center justify-between text-center shadow-[0_0_30px_rgba(255,42,141,0.15)] group transition-all"
                >
                  <div className="h-28 w-full flex items-center justify-center p-2">
                    {p.website ? (
                      <a
                        href={p.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="transition-transform group-hover:scale-105"
                      >
                        <WhiteLogo
                          src={p.logo?.[0]}
                          alt={p.partnernavn}
                          className="max-h-20 max-w-full object-contain"
                        />
                      </a>
                    ) : (
                      <WhiteLogo
                        src={p.logo?.[0]}
                        alt={p.partnernavn}
                        className="max-h-20 max-w-full object-contain"
                      />
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-800 w-full">
                    <h4 className="text-xl font-bold text-white font-display uppercase">{p.partnernavn}</h4>
                    <p className="text-xs text-slate-400 mt-1">{p.beskrivelse}</p>
                    {p.website && (
                      <a
                        href={p.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-pink-400 hover:text-pink-300 mt-3"
                      >
                        <span>Besøg website</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true"></i>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Elite Sponsors */}
        {elite.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 border-b border-purple-500/30 pb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span>
              <h3 className="text-xl sm:text-2xl font-black font-display text-white uppercase">
                Elite Sponsors
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {elite.map((p, i) => (
                <div
                  key={p.id || i}
                  className="bg-[#12141f] border border-slate-800 rounded-2xl p-6 flex flex-col items-center justify-between text-center group hover:border-purple-500/40 transition-colors"
                >
                  <div className="h-20 w-full flex items-center justify-center p-2">
                    {p.website ? (
                      <a
                        href={p.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="transition-transform group-hover:scale-105"
                      >
                        <WhiteLogo
                          src={p.logo?.[0]}
                          alt={p.partnernavn}
                          className="max-h-16 max-w-full object-contain"
                        />
                      </a>
                    ) : (
                      <WhiteLogo
                        src={p.logo?.[0]}
                        alt={p.partnernavn}
                        className="max-h-16 max-w-full object-contain"
                      />
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-800 w-full">
                    <h4 className="text-lg font-bold text-white font-display uppercase">{p.partnernavn}</h4>
                    <p className="text-xs text-slate-400 mt-1">{p.beskrivelse}</p>
                    {p.website && (
                      <a
                        href={p.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-400 hover:text-purple-300 mt-2"
                      >
                        <span>Besøg website</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true"></i>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Core & Supporter Sponsors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {/* Core */}
          {core.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg sm:text-xl font-bold font-display text-white uppercase border-b border-slate-800 pb-2">
                Core Sponsors
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {core.map((p, i) => (
                  <div
                    key={p.id || i}
                    className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 flex flex-col items-center justify-center h-28 group hover:border-slate-700 transition-colors"
                  >
                    {p.website ? (
                      <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-center group-hover:scale-105 transition-transform flex flex-col items-center justify-center">
                        <WhiteLogo src={p.logo?.[0]} alt={p.partnernavn} className="max-h-12 max-w-full object-contain mx-auto" />
                        <span className="block text-[10px] text-slate-400 mt-1 truncate">{p.partnernavn}</span>
                      </a>
                    ) : (
                      <div className="text-center flex flex-col items-center justify-center">
                        <WhiteLogo src={p.logo?.[0]} alt={p.partnernavn} className="max-h-12 max-w-full object-contain mx-auto" />
                        <span className="block text-[10px] text-slate-400 mt-1 truncate">{p.partnernavn}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Supporter */}
          {supporter.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg sm:text-xl font-bold font-display text-white uppercase border-b border-slate-800 pb-2">
                Supporter Sponsors
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {supporter.map((p, i) => (
                  <div
                    key={p.id || i}
                    className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 flex flex-col items-center justify-center h-28 group hover:border-slate-700 transition-colors"
                  >
                    {p.website ? (
                      <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-center group-hover:scale-105 transition-transform flex flex-col items-center justify-center">
                        <WhiteLogo src={p.logo?.[0]} alt={p.partnernavn} className="max-h-12 max-w-full object-contain mx-auto" />
                        <span className="block text-[10px] text-slate-400 mt-1 truncate">{p.partnernavn}</span>
                      </a>
                    ) : (
                      <div className="text-center flex flex-col items-center justify-center">
                        <WhiteLogo src={p.logo?.[0]} alt={p.partnernavn} className="max-h-12 max-w-full object-contain mx-auto" />
                        <span className="block text-[10px] text-slate-400 mt-1 truncate">{p.partnernavn}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bliv Partner / Sponsor boks */}
        <div className="bg-gradient-to-r from-pink-950/40 via-purple-950/30 to-slate-900 border border-pink-500/30 rounded-3xl p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <span className="text-xs uppercase font-mono text-pink-400 font-bold">Vil du være med?</span>
            <h3 className="text-2xl sm:text-3xl font-black text-white font-display uppercase">
              Bliv sponsor eller partner til ØrBike
            </h3>
            <p className="text-slate-300 text-sm max-w-xl">
              Få stærk synlighed foran 1.000 deltagere og på sociale medier, og vær samtidig med til at donere til børn med kritisk sygdom.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/priority-cykler"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-xs uppercase tracking-wider shadow-lg hover:scale-105 transition-transform"
            >
              <span>Se Priority Cykler</span>
              <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
            </Link>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 font-bold text-xs uppercase tracking-wider transition-colors"
            >
              <span>Kontakt os</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
